import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Button, Input, Modal, Popover, message } from 'antd';
import { useState, useEffect } from 'react';
import { deleteSensitiveWord } from './api';
import { AddForm, EditModal } from './components/SensitiveWordForm';
import SensitiveWordTable from './components/SensitiveWordTable';
import useSensitiveWords from './hooks/useSensitiveWords';
import { SensitiveWordItem } from './types';
import styled from '@emotion/styled';
import useModal from 'hooks/useModal';

const SensitiveWord = () => {
  const { t } = useTranslate();

  const [searchValue, setSearchValue] = useState('');

  const { visible, show, hide, meta } = useModal<SensitiveWordItem>();

  const {
    list,
    total = 0,
    loading,
    keyword,
    pagination,
    onSearch,
    onPageChange,
    refresh,
  } = useSensitiveWords();

  useEffect(() => {
    setSearchValue(keyword);
  }, [keyword]);

  const handleDelete = (item: SensitiveWordItem) => {
    return new Promise<void>((resolve, reject) => {
      Modal.confirm({
        title: t('确认要删除吗'),
        content: t('删除后不可恢复'),
        onCancel: () => reject(),
        onOk: () => {
          return deleteSensitiveWord(item.id)
            .then(() => {
              message.success(t('已删除'));
              refresh();
              resolve();
            })
            .catch(() => {
              message.error(t('translation.操作失败，请稍后再试'));
              reject();
            });
        },
      });
    });
  };

  return (
    <Container>
      <header>
        <div>
          <h3>{t('scene.managementCenter.navigation.store.restrictedWords')}</h3>
          <span>{t('敏感词副标题')}</span>
        </div>
        <Popover
          placement="bottomRight"
          content={
            <div className="w-60 whitespace-break-spaces break-words">{t('敏感词使用说明')}</div>
          }
        >
          <Button className="!py-0 !h-7">
            {t('功能说明')} <QuestionCircleOutlined />
          </Button>
        </Popover>
      </header>
      <div>
        <div className="relative font-bold before:absolute before:left-0 before:top-1 before:content-[''] before:border-solid before:w-1 before:h-[14px] before:bg-primary before:border-0 pl-2">
          {t('添加敏感词')}
        </div>
        <div className="w-full pt-4">
          {total >= 200 ? (
            <div className="text-sm text-[#84868C] mt-2">
              {t('敏感词数量已达上限，无法继续添加')}
            </div>
          ) : (
            <AddForm
              onCheckWork={(word) => {
                setSearchValue(word);
                onSearch(word);
              }}
              onUpdate={refresh}
            />
          )}
        </div>
      </div>
      <section>
        <header className="flex items-center justify-between mb-4">
          <div className="relative font-bold before:absolute before:left-0 before:top-1 before:content-[''] before:border-solid before:w-1 before:h-[14px] before:bg-primary before:border-0 pl-2">
            {t('敏感词列表')}
          </div>
          <div className="flex items-center gap-2">
            <Input
              className="w-[320px]"
              value={searchValue}
              onChange={(e) => {
                const value = e.target.value;
                setSearchValue(value);
                if (!value && e.type !== 'change') {
                  onSearch(value);
                }
              }}
              onPressEnter={() => onSearch(searchValue)}
              placeholder={t('department.请输入x', { x: t('敏感词内容') })}
              prefix={<SearchOutlined />}
              allowClear
            />
            <Button type="primary" onClick={() => onSearch(searchValue)}>
              {t('department.搜索')}
            </Button>
          </div>
        </header>
        <div>
          <SensitiveWordTable
            dataSource={list}
            loading={loading}
            pagination={
              total > 0
                ? {
                    current: pagination.pageNum,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    showTotal: (totalCount) => `共 ${totalCount} 条`,
                    onChange: onPageChange,
                  }
                : false
            }
            onEdit={show}
            onDelete={handleDelete}
          />
        </div>
      </section>
      {meta && (
        <EditModal
          visible={visible}
          onCancel={() => hide()}
          sensitiveWordItem={meta}
          onUpdate={refresh}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  > * {
    flex: 0 0 auto;
    padding: 18px 24px;
    background: var(--fett-bg-panel);
  }
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 56px;
    margin-bottom: 1px;
    > div {
      flex: 1 0 0;
      display: flex;
      align-items: center;
      > h3 {
        flex: 0 0 auto;
        margin-bottom: 0;
        [data-color-mode='dark'] & {
          color: var(--fett-text-primary);
        }
      }
      > span {
        margin-left: 8px;
        font-size: 12px;
      }
    }
  }
  > div {
    margin-bottom: 16px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    > header {
      flex: 0 0 auto;
    }
    > div {
      flex: 1 0 0;
    }
  }
`;

export default SensitiveWord;
